import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, MessageSquare } from 'lucide-react';
import type { CampaignComment, CampaignDetail } from '../../shared/types';
import { api, patch, qk, withShare } from '../lib/api';
import { ACTOR_LABEL } from '../lib/activity';
import { formatName, formatTimecode, relativeTime } from '../lib/format';
import { BannerThumb } from './BannerMedia';
import { errorMessage, useToast } from './toast';
import { EmptyState, Notice, Spinner, StatusChip } from './ui';

type Filter = 'open' | 'resolved' | 'all';

/**
 * Alle Kommentare einer Kampagne auf einer Seite, nach Motiv sortiert. Die Nummern entsprechen
 * den Pins am Motiv; „Am Motiv zeigen“ öffnet die Review-Ansicht genau an dieser Stelle.
 */
export function CommentsOverview({ campaign, base }: { campaign: CampaignDetail; base: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>('open');
  // Standard: alle offenen – dann stimmt die Zahl mit dem Reiter überein; ältere Versionen sind markiert.
  const [currentOnly, setCurrentOnly] = useState(false);
  const comments = useQuery({
    queryKey: qk('campaign-comments', campaign.id),
    queryFn: () => api<CampaignComment[]>(`/api/campaigns/${campaign.id}/comments`),
  });

  // In der Kundenansicht der Agentur interne Kommentare ausblenden – wie beim Kunden.
  const scoped = (comments.data ?? []).filter(
    (c) => (!currentOnly || c.isCurrentVersion) && (campaign.permissions.canSeeInternal || !c.internal),
  );
  const counts = {
    open: scoped.filter((c) => !c.resolvedAt).length,
    resolved: scoped.filter((c) => c.resolvedAt).length,
    all: scoped.length,
  };
  const visible = scoped.filter((c) =>
    filter === 'all' ? true : filter === 'open' ? !c.resolvedAt : Boolean(c.resolvedAt),
  );

  const groups = useMemo(() => {
    const order = new Map(campaign.banners.map((b, i) => [b.id, i]));
    const map = new Map<string, CampaignComment[]>();
    for (const c of visible) map.set(c.bannerId, [...(map.get(c.bannerId) ?? []), c]);
    return [...map.entries()].sort((a, b) => (order.get(a[0]) ?? 0) - (order.get(b[0]) ?? 0));
  }, [visible, campaign.banners]);

  const toggleResolved = async (c: CampaignComment) => {
    try {
      await patch(`/api/comments/${c.id}`, { resolved: !c.resolvedAt });
      await queryClient.invalidateQueries({ queryKey: qk('campaign-comments', campaign.id) });
      await queryClient.invalidateQueries({ queryKey: qk('campaign', campaign.id) });
      await queryClient.invalidateQueries({ queryKey: qk('comments', c.bannerId) });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  if (comments.isLoading) return <Spinner />;
  if (comments.error) return <Notice kind="error">{errorMessage(comments.error)}</Notice>;

  return (
    <div className="comments-overview">
      <div className="collection-bar">
        <div className="segmented" role="group" aria-label="Kommentare filtern">
          {(['open', 'resolved', 'all'] as Filter[]).map((f) => (
            <button key={f} aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {f === 'open' ? 'Offen' : f === 'resolved' ? 'Erledigt' : 'Alle'} ({counts[f]})
            </button>
          ))}
        </div>
        <label className="check small">
          <input type="checkbox" checked={currentOnly} onChange={(e) => setCurrentOnly(e.target.checked)} />
          <span>Nur aktuelle Versionen</span>
        </label>
        <span className="spacer" />
        {(comments.data?.length ?? 0) > 0 && (
          <a className="btn btn-ghost btn-sm" href={withShare(`/api/campaigns/${campaign.id}/comments.csv`)}>
            <Download size={15} /> Als Tabelle (CSV)
          </a>
        )}
      </div>

      {groups.length === 0 && (
        <EmptyState
          icon={<MessageSquare size={30} />}
          title={filter === 'open' ? 'Keine offenen Kommentare' : 'Keine Kommentare'}
        >
          <p>
            {filter === 'open' && counts.all > 0
              ? 'Alles erledigt – unter „Alle“ steht der Verlauf.'
              : 'Kommentare zu allen Motiven erscheinen hier gesammelt.'}
          </p>
        </EmptyState>
      )}

      {groups.map(([bannerId, items]) => {
        const banner = campaign.banners.find((b) => b.id === bannerId);
        const first = items[0];
        return (
          <section key={bannerId} className="co-banner">
            <Link className="co-banner-head" to={`${base}/motiv/${bannerId}`}>
              <div className="co-thumb">
                {banner?.current && <BannerThumb version={banner.current} maxWidth={96} maxHeight={64} />}
              </div>
              <div className="grow" style={{ minWidth: 0 }}>
                <strong className="co-title">{first.bannerName}</strong>
                <span className="small muted">
                  {formatName(first.width, first.height)}
                  {first.width ? ` · ${first.width}×${first.height}` : ''}
                </span>
              </div>
              {banner && <StatusChip status={banner.status} />}
            </Link>
            <ol className="co-list">
              {items.map((c) => (
                <li
                  key={c.id}
                  className={`co-item ${c.resolvedAt ? 'is-resolved' : ''} ${c.internal ? 'is-internal' : ''}`}
                >
                  <span className="pin-num" title="Nummer am Motiv">
                    {c.number}
                  </span>
                  <div className="co-body">
                    <div className="co-meta">
                      <strong>{c.authorName}</strong>
                      <span className="chip chip-outline" style={{ height: 20, fontSize: 10.5 }}>
                        {ACTOR_LABEL[c.authorKind]}
                      </span>
                      {c.internal && <span className="chip chip-internal">intern</span>}
                      <span className="tiny muted">
                        v{c.versionNumber}
                        {!c.isCurrentVersion && ' (ältere Version)'} · {relativeTime(c.createdAt)}
                        {c.timeMs !== null && ` · bei ${formatTimecode(c.timeMs)}`}
                      </span>
                    </div>
                    <p className="co-text">{c.body}</p>
                    {c.replies.length > 0 && (
                      <ul className="co-replies">
                        {c.replies
                          .filter((r) => campaign.permissions.canSeeInternal || !r.internal)
                          .map((r) => (
                            <li key={r.id}>
                              <strong>{r.authorName}:</strong> {r.body}
                            </li>
                          ))}
                      </ul>
                    )}
                    <div className="row-wrap" style={{ gap: 14 }}>
                      <Link className="link-arrow small" to={`${base}/motiv/${bannerId}?kommentar=${c.id}`}>
                        Am Motiv zeigen
                      </Link>
                      {(campaign.permissions.canManage || c.isOwn) && (
                        <button className="text-link small" onClick={() => toggleResolved(c)}>
                          {c.resolvedAt ? 'Wieder öffnen' : 'Als erledigt markieren'}
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        );
      })}
    </div>
  );
}
